import { css, cx } from "@emotion/css";
import { ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useVirtualizer } from "@tanstack/react-virtual";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState, useRef, useMemo } from "react";
import type { ForwardedRef, ReactElement } from "react";
import type React from "react";
import { useObserveElementSize } from "../../../hooks/useObserveElementSize";
import { resetStyles } from "../../../utils";
import { Button } from "../../Button";
import { CircularProgress } from "../../Progress/CircularProgress";
import { remToPx } from "../utils/remToPx";
export interface ListboxOption {
    label: string;
    value: string;
}
export interface ListboxElement {
    handleKeyDown: (event: React.KeyboardEvent) => void;
}
interface ListboxProps<T extends ListboxOption> {
    id?: string;
    value: string | string[] | undefined;
    options: T[];
    accessibleName: string;
    renderOption: (option: T, selected: boolean) => ReactElement;
    getOptionHeight: (option: T) => number;
    onOptionSelected: (option: T) => void;
    getOptionHtmlId: (index: number) => string;
    onOptionActive?: (index: number) => void;
    onLoadMore?: () => void;
    isLoading?: boolean;
    hasError?: boolean;
    isMultiSelectable?: boolean;
    scrollPaddingTop?: number;
    scrollPaddingBottom?: number;
    /**
     * Focus behaviour of the listbox changes depending on whether there is an element like a search input controlling the focus
     *
     * `focus`: The option elements are independently focusable and become focused when they are the active selection.
     * `sub-focus`: The option elements are not directly focusable so that an element in the consumer can retain focus. Active selection should be specified in this consumer element with aria-activedescendant.
     * `none`:  When the type is `sub-focus` and the controlling consumer element is not focused, `none` can be used to disable the sub-focus appearance in the listbox.
     */
    focusType: "focus" | "sub-focus" | "none";
}
function ListboxInner<T extends ListboxOption>({ id, value, options, accessibleName, renderOption, getOptionHeight, onOptionSelected, getOptionHtmlId, onOptionActive, onLoadMore, isLoading, hasError, isMultiSelectable, scrollPaddingTop, scrollPaddingBottom, focusType }: ListboxProps<T>, ref: ForwardedRef<ListboxElement | null>) {
    const [footerElement, setFooterElement] = useState<HTMLLIElement | null>(null);
    const [footerHeight, setFooterHeight] = useState(0);
    const selectedValues = useMemo(() => {
        return new Set(Array.isArray(value) ? value : [value]);
    }, [value]);
    const interactionMode = useRef<"keyboard" | "mouse" | undefined>(undefined);
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(Math.max(options.findIndex((option) => selectedValues.has(option.value)), 0));
    const rowVirtualizer = useVirtualizer({
        count: options.length,
        getScrollElement: () => containerRef.current,
        getItemKey: (index) => options[index].value,
        estimateSize: (index) => getOptionHeight(options[index]),
        gap: remToPx(space[4]),
        paddingStart: scrollPaddingTop ?? remToPx(space[6]),
        paddingEnd: scrollPaddingBottom ?? remToPx(space[6]),
        overscan: 5,
    });
    const handleActiveIndexChange = useCallback((newActiveIndex: number) => {
        setActiveIndex(newActiveIndex);
        const getOption = () => {
            const optionHtmlId = getOptionHtmlId(newActiveIndex);
            return document.getElementById(optionHtmlId);
        };
        if (interactionMode.current !== "mouse") {
            const option = getOption();
            if (option) {
                option.scrollIntoView({ block: "nearest" });
            }
            else {
                rowVirtualizer.scrollToIndex(newActiveIndex);
            }
        }
    }, [getOptionHtmlId, rowVirtualizer]);
    if (options.length > 0 && activeIndex >= options.length) {
        handleActiveIndexChange(0);
    }
    useEffect(() => {
        onOptionActive?.(activeIndex);
    }, [activeIndex, onOptionActive]);
    useEffect(() => {
        rowVirtualizer.scrollToIndex(activeIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        interactionMode.current = "keyboard";
        const { key } = event;
        switch (key) {
            case "ArrowUp":
                event.preventDefault();
                if (activeIndex > 0) {
                    handleActiveIndexChange(activeIndex - 1);
                }
                else {
                    rowVirtualizer.scrollToOffset(0);
                }
                break;
            case "ArrowDown":
                event.preventDefault();
                if (activeIndex < options.length - 1) {
                    handleActiveIndexChange(activeIndex + 1);
                }
                else {
                    rowVirtualizer.scrollToOffset(rowVirtualizer.getTotalSize());
                }
                break;
            case "Home":
            case "End":
                event.preventDefault();
                handleActiveIndexChange(key === "Home" ? 0 : options.length - 1);
                break;
            case "Enter":
            case " ":
                event.preventDefault();
                if (activeIndex >= 0 && activeIndex < options.length) {
                    onOptionSelected(options[activeIndex]);
                }
                break;
        }
    }, [activeIndex, handleActiveIndexChange, onOptionSelected, options, rowVirtualizer]);
    useImperativeHandle(ref, () => ({
        handleKeyDown,
    }), [handleKeyDown]);
    const onActiveOptionRef = useCallback((element: HTMLElement | null) => {
        if (focusType === "focus") {
            element?.focus({ preventScroll: true });
        }
    }, [focusType]);
    // Use a ref to remove loadMore as a dependency of the effect. We only want it to run when isLoading changes, so that loadMore can trigger once per load.
    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;
    useEffect(() => {
        const container = containerRef.current;
        if (isLoading || !onLoadMoreRef.current || !container || hasError)
            return;
        // "Near" is defined here as having less than the client height of scrollable area remaining to scroll
        const isNearEndOfList = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const remainingScroll = scrollHeight - scrollTop - clientHeight;
            return remainingScroll <= clientHeight;
        };
        if (isNearEndOfList()) {
            onLoadMoreRef?.current();
            return;
        }
        const handleScroll = () => {
            if (isNearEndOfList()) {
                onLoadMoreRef?.current?.();
                container.removeEventListener("scroll", handleScroll);
            }
        };
        container.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [hasError, isLoading]);
    useObserveElementSize(footerElement, ({ height }) => {
        setFooterHeight(height);
    }, () => {
        setFooterHeight(0);
    });
    const showFooter = isLoading || hasError;
    const scrollHeight = showFooter ? rowVirtualizer.getTotalSize() + footerHeight : rowVirtualizer.getTotalSize();
    const scrollHeightStyles = { height: `${scrollHeight}px` };
    return (<div ref={containerRef} className={listboxContainerStyles} onKeyDown={handleKeyDown}>
            <ul id={id} className={listboxStyles} style={scrollHeightStyles} role="listbox" aria-label={accessibleName} aria-multiselectable={isMultiSelectable ? true : undefined}>
                {rowVirtualizer.getVirtualItems().map(({ key, index, size, start }) => {
            const virtualRowSizeStartStyles = {
                height: `${size}px`,
                transform: `translateY(${start}px)`,
            } as const;
            const option = options[index];
            const selected = selectedValues.has(option.value);
            return (<li key={key} ref={activeIndex === index ? onActiveOptionRef : undefined} id={getOptionHtmlId(index)} role="option" tabIndex={focusType === "focus" && activeIndex === index ? 0 : -1} aria-selected={selected} className={cx(virtualRowStyles, optionStyles, {
                    [activeOptionStyles]: focusType === "sub-focus" && activeIndex === index,
                })} style={virtualRowSizeStartStyles} onClick={() => onOptionSelected(option)} onMouseDown={(event: React.MouseEvent) => {
                    if (focusType !== "focus") {
                        event.preventDefault();
                    }
                }} onMouseMove={() => {
                    if (interactionMode.current !== "mouse") {
                        interactionMode.current = "mouse";
                        handleActiveIndexChange(index);
                    }
                }} onMouseEnter={() => {
                    if (interactionMode.current === "mouse") {
                        handleActiveIndexChange(index);
                    }
                }}>
                            {renderOption(option, selected)}
                        </li>);
        })}
                {showFooter && (<li key="footer" ref={setFooterElement} className={footerStyles}>
                        {isLoading && <CircularProgress size="small"/>}
                        {hasError && (<>
                                <span className={errorStyles}>
                                    <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.warning}/> Failed to load results
                                </span>
                                <Button label="Retry" importance="quiet" onClick={() => onLoadMoreRef.current?.()}/>
                            </>)}
                    </li>)}
            </ul>
        </div>);
}
// forwardRef doesn't like function components with generics, so this assertion is required
// revisit this in React 19
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const Listbox = forwardRef(ListboxInner) as <T extends ListboxOption>(props: ListboxProps<T> & {
    ref?: React.Ref<ListboxElement>;
}) => React.ReactElement;
const listboxContainerStyles = css({
    margin: 0,
    overflowY: "auto",
});
const listboxStyles = css({
    ...resetStyles.list,
    display: "flex",
    flexDirection: "column",
    position: "relative",
});
const virtualRowStyles = css({
    position: "absolute",
    top: 0,
    left: space[6],
    right: space[6],
});
const optionStyles = css({
    listStyleType: "none",
    flexShrink: 0,
    cursor: "pointer",
    borderRadius: borderRadius.small,
    scrollMargin: `${space[8]} 0`,
    ":focus-visible": {
        outline: "none",
    },
    "&:hover, &:focus": {
        backgroundColor: themeTokens.color.menuList.background.hover,
    },
    "&:active": {
        backgroundColor: themeTokens.color.menuList.background.active,
    },
});
const activeOptionStyles = css({
    backgroundColor: themeTokens.color.menuList.background.hover,
});
const footerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: space[4],
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    width: "100%",
    boxSizing: "border-box",
    bottom: space[8], // should be same as list bottom padding in virtualizer
    paddingTop: space[8], // should be same as gap in virtualizer
});
const errorStyles = css({
    display: "flex",
    color: themeTokens.color.text.secondary,
    font: text.body.regular.large,
    gap: space[6],
    alignItems: "center",
});
